// Saskatchewan and Alberta cities data for SEO location pages

export interface CityData {
  id: string;
  name: string;
  province: string;
  provinceCode: string;
  slug: string;
  population: number;
  avgHouseholdIncome: number;
  majorIndustries: string[];
  taxRates: {
    provincial: number;
    federal: number;
  };
  description: string;
  marketInsights: {
    economicGrowth: string;
    keyEmployers: string[];
    investmentOpportunities: string;
    demographics: string;
  };
  seoMetadata: {
    title: string;
    description: string;
    keywords: string[];
  };
}

export const saskAlbertaCities: CityData[] = [
  // Alberta Cities
  {
    id: 'calgary',
    name: 'Calgary',
    province: 'Alberta',
    provinceCode: 'AB',
    slug: 'calgary',
    population: 1336000,
    avgHouseholdIncome: 102000,
    majorIndustries: ['Energy', 'Financial Services', 'Technology', 'Transportation'],
    taxRates: {
      provincial: 0.10,
      federal: 0.15
    },
    description: 'Calgary is Alberta\'s largest city and the energy capital of Canada, offering diverse economic opportunities beyond oil and gas.',
    marketInsights: {
      economicGrowth: 'Calgary is experiencing economic diversification with growth in tech, finance, and renewable energy sectors.',
      keyEmployers: ['Suncor Energy', 'TC Energy', 'Canadian Pacific Railway', 'Shaw Communications'],
      investmentOpportunities: 'Strong opportunities in energy transition, technology startups, and real estate development.',
      demographics: 'Young, educated workforce with median age of 37 and high proportion of university graduates.'
    },
    seoMetadata: {
      title: 'Financial Advisor Calgary | Wealth Management & Insurance',
      description: 'Expert financial planning services in Calgary. Specialized strategies for energy professionals, business owners, and high-net-worth individuals.',
      keywords: ['Calgary financial advisor', 'Calgary wealth management', 'Calgary financial planner', 'Alberta financial advisor']
    }
  },
  {
    id: 'edmonton',
    name: 'Edmonton',
    province: 'Alberta',
    provinceCode: 'AB',
    slug: 'edmonton',
    population: 1010000,
    avgHouseholdIncome: 94000,
    majorIndustries: ['Government', 'Education', 'Health Care', 'Oil & Gas'],
    taxRates: {
      provincial: 0.10,
      federal: 0.15
    },
    description: 'Edmonton, Alberta\'s capital city, combines government stability with energy sector opportunities and a growing tech scene.',
    marketInsights: {
      economicGrowth: 'Steady growth driven by public sector employment, post-secondary institutions, and energy sector recovery.',
      keyEmployers: ['Government of Alberta', 'University of Alberta', 'Alberta Health Services', 'Stantec'],
      investmentOpportunities: 'Opportunities in health sciences, AI research, clean technology, and urban development.',
      demographics: 'Diverse population with strong representation in government, healthcare, and education sectors.'
    },
    seoMetadata: {
      title: 'Financial Advisor Edmonton | Investment & Tax Planning',
      description: 'Professional financial advisory services in Edmonton. Expert guidance for government employees, healthcare workers, and business professionals.',
      keywords: ['Edmonton financial advisor', 'Edmonton wealth management', 'Edmonton financial planning', 'Alberta investment advisor']
    }
  },
  {
    id: 'red-deer',
    name: 'Red Deer',
    province: 'Alberta',
    provinceCode: 'AB',
    slug: 'red-deer',
    population: 106000,
    avgHouseholdIncome: 88000,
    majorIndustries: ['Oil & Gas', 'Agriculture', 'Manufacturing', 'Retail'],
    taxRates: {
      provincial: 0.10,
      federal: 0.15
    },
    description: 'Red Deer serves as central Alberta\'s economic hub, strategically located between Calgary and Edmonton.',
    marketInsights: {
      economicGrowth: 'Benefiting from its central location and diverse economy spanning energy, agriculture, and manufacturing.',
      keyEmployers: ['Nova Chemicals', 'Peavey Industries', 'Red Deer Regional Hospital', 'Bower Place Shopping Centre'],
      investmentOpportunities: 'Growth in petrochemicals, agri-business, and logistics due to strategic location.',
      demographics: 'Young families and professionals attracted by affordable housing and economic opportunities.'
    },
    seoMetadata: {
      title: 'Financial Advisor Red Deer | Central Alberta Wealth Management',
      description: 'Expert financial planning in Red Deer. Specialized services for energy workers, agricultural businesses, and growing families.',
      keywords: ['Red Deer financial advisor', 'Red Deer financial planning', 'Central Alberta wealth management', 'Red Deer investment advisor']
    }
  },
  {
    id: 'lethbridge',
    name: 'Lethbridge',
    province: 'Alberta',
    provinceCode: 'AB',
    slug: 'lethbridge',
    population: 101000,
    avgHouseholdIncome: 82000,
    majorIndustries: ['Agriculture', 'Education', 'Manufacturing', 'Renewable Energy'],
    taxRates: {
      provincial: 0.10,
      federal: 0.15
    },
    description: 'Lethbridge is southern Alberta\'s commercial, financial, and industrial centre with a strong agricultural base.',
    marketInsights: {
      economicGrowth: 'Growing renewable energy sector, particularly wind power, complementing traditional agricultural economy.',
      keyEmployers: ['University of Lethbridge', 'Lethbridge College', 'Alberta Health Services', 'Richardson International'],
      investmentOpportunities: 'Renewable energy projects, agri-tech innovations, and food processing industries.',
      demographics: 'Mix of students, agricultural professionals, and growing tech sector workers.'
    },
    seoMetadata: {
      title: 'Financial Advisor Lethbridge | Southern Alberta Financial Planning',
      description: 'Professional wealth management in Lethbridge. Expert advice for agricultural businesses, educators, and renewable energy professionals.',
      keywords: ['Lethbridge financial advisor', 'Lethbridge financial planning', 'Southern Alberta wealth management', 'Lethbridge investment services']
    }
  },
  {
    id: 'medicine-hat',
    name: 'Medicine Hat',
    province: 'Alberta',
    provinceCode: 'AB',
    slug: 'medicine-hat',
    population: 63000,
    avgHouseholdIncome: 85000,
    majorIndustries: ['Natural Gas', 'Manufacturing', 'Agriculture', 'Tourism'],
    taxRates: {
      provincial: 0.10,
      federal: 0.15
    },
    description: 'Medicine Hat, known as the "Gas City," offers unique advantages with abundant natural gas resources and sunshine.',
    marketInsights: {
      economicGrowth: 'Leveraging natural gas advantages for industrial growth while developing renewable energy projects.',
      keyEmployers: ['Methanex', 'Goodyear Canada', 'Canadian Fertilizers Limited', 'City of Medicine Hat'],
      investmentOpportunities: 'Industrial development, greenhouse operations, and solar energy projects.',
      demographics: 'Stable population with mix of industrial workers, retirees, and small business owners.'
    },
    seoMetadata: {
      title: 'Financial Advisor Medicine Hat | Investment Planning Alberta',
      description: 'Expert financial advisory services in Medicine Hat. Specialized planning for industrial workers, business owners, and retirees.',
      keywords: ['Medicine Hat financial advisor', 'Medicine Hat wealth management', 'Medicine Hat financial planning', 'Gas City investment advisor']
    }
  },

  // Saskatchewan Cities
  {
    id: 'saskatoon',
    name: 'Saskatoon',
    province: 'Saskatchewan',
    provinceCode: 'SK',
    slug: 'saskatoon',
    population: 330000,
    avgHouseholdIncome: 88000,
    majorIndustries: ['Agriculture', 'Mining', 'Biotechnology', 'Education'],
    taxRates: {
      provincial: 0.105,
      federal: 0.15
    },
    description: 'Saskatoon is Saskatchewan\'s largest city and economic engine, known for innovation in agriculture and mining technology.',
    marketInsights: {
      economicGrowth: 'Strong growth in agri-tech, potash mining, and uranium sectors driving economic expansion.',
      keyEmployers: ['Nutrien', 'Cameco', 'University of Saskatchewan', 'Saskatchewan Health Authority'],
      investmentOpportunities: 'Agricultural technology, mining innovation, and biotechnology research and development.',
      demographics: 'Young, educated population with strong representation in STEM fields and agriculture.'
    },
    seoMetadata: {
      title: 'Financial Advisor Saskatoon | Saskatchewan Wealth Management',
      description: 'Premier financial planning services in Saskatoon. Expert strategies for mining professionals, agricultural businesses, and tech entrepreneurs.',
      keywords: ['Saskatoon financial advisor', 'Saskatoon wealth management', 'Saskatchewan financial planner', 'Saskatoon investment advisor']
    }
  },
  {
    id: 'regina',
    name: 'Regina',
    province: 'Saskatchewan',
    provinceCode: 'SK',
    slug: 'regina',
    population: 263000,
    avgHouseholdIncome: 92000,
    majorIndustries: ['Government', 'Oil & Gas', 'Agriculture', 'Finance'],
    taxRates: {
      provincial: 0.105,
      federal: 0.15
    },
    description: 'Regina, Saskatchewan\'s capital, combines government stability with strong energy and agricultural sectors.',
    marketInsights: {
      economicGrowth: 'Steady growth supported by provincial government, Crown corporations, and energy sector.',
      keyEmployers: ['Government of Saskatchewan', 'SaskPower', 'SaskTel', 'Co-op Refinery Complex'],
      investmentOpportunities: 'Energy sector, agricultural value-added processing, and technology services.',
      demographics: 'Stable population with high proportion of government employees and energy professionals.'
    },
    seoMetadata: {
      title: 'Financial Advisor Regina | Investment Planning Saskatchewan',
      description: 'Expert financial advisory services in Regina. Specialized planning for government employees, energy professionals, and business owners.',
      keywords: ['Regina financial advisor', 'Regina wealth management', 'Saskatchewan financial planning', 'Regina investment services']
    }
  },
  {
    id: 'prince-albert',
    name: 'Prince Albert',
    province: 'Saskatchewan',
    provinceCode: 'SK',
    slug: 'prince-albert',
    population: 38000,
    avgHouseholdIncome: 75000,
    majorIndustries: ['Forestry', 'Agriculture', 'Tourism', 'Mining'],
    taxRates: {
      provincial: 0.105,
      federal: 0.15
    },
    description: 'Prince Albert serves as the gateway to Saskatchewan\'s north, with diverse natural resource industries.',
    marketInsights: {
      economicGrowth: 'Growth in forestry, mining exploration, and tourism sectors accessing northern wilderness.',
      keyEmployers: ['Paper Excellence', 'Victoria Hospital', 'Saskatchewan Penitentiary', 'Northern Lights Casino'],
      investmentOpportunities: 'Forestry products, northern mining development, and eco-tourism ventures.',
      demographics: 'Mix of resource sector workers, Indigenous communities, and service professionals.'
    },
    seoMetadata: {
      title: 'Financial Advisor Prince Albert | Northern Saskatchewan Planning',
      description: 'Professional financial planning in Prince Albert. Expert advice for forestry workers, mining professionals, and small businesses.',
      keywords: ['Prince Albert financial advisor', 'Prince Albert wealth management', 'Northern Saskatchewan financial planning', 'Prince Albert investment']
    }
  },
  {
    id: 'moose-jaw',
    name: 'Moose Jaw',
    province: 'Saskatchewan',
    provinceCode: 'SK',
    slug: 'moose-jaw',
    population: 34000,
    avgHouseholdIncome: 73000,
    majorIndustries: ['Agriculture', 'Manufacturing', 'Tourism', 'Transportation'],
    taxRates: {
      provincial: 0.105,
      federal: 0.15
    },
    description: 'Moose Jaw combines agricultural heritage with growing tourism and manufacturing sectors.',
    marketInsights: {
      economicGrowth: 'Diversifying economy with growth in tourism, food processing, and transportation logistics.',
      keyEmployers: ['CFB Moose Jaw', 'Mosaic Company', 'Pioneer Co-op', 'Temple Gardens Mineral Spa'],
      investmentOpportunities: 'Tourism development, agricultural processing, and transportation hub services.',
      demographics: 'Military families, agricultural workers, and growing retiree population.'
    },
    seoMetadata: {
      title: 'Financial Advisor Moose Jaw | Saskatchewan Investment Planning',
      description: 'Expert financial services in Moose Jaw. Specialized planning for military families, agricultural businesses, and retirees.',
      keywords: ['Moose Jaw financial advisor', 'Moose Jaw financial planning', 'Moose Jaw wealth management', 'Saskatchewan investment advisor']
    }
  },
  {
    id: 'swift-current',
    name: 'Swift Current',
    province: 'Saskatchewan',
    provinceCode: 'SK',
    slug: 'swift-current',
    population: 18000,
    avgHouseholdIncome: 78000,
    majorIndustries: ['Agriculture', 'Oil & Gas', 'Renewable Energy', 'Livestock'],
    taxRates: {
      provincial: 0.105,
      federal: 0.15
    },
    description: 'Swift Current is a key agricultural and energy hub in southwest Saskatchewan.',
    marketInsights: {
      economicGrowth: 'Growth driven by oil and gas activity, wind energy development, and agricultural innovation.',
      keyEmployers: ['Suncor Energy', 'SaskPower Wind Facilities', 'Cypress Health Region', 'Pioneer Co-op'],
      investmentOpportunities: 'Renewable energy projects, oil and gas services, and agricultural technology.',
      demographics: 'Energy workers, farmers, ranchers, and service industry professionals.'
    },
    seoMetadata: {
      title: 'Financial Advisor Swift Current | Southwest Saskatchewan Planning',
      description: 'Professional wealth management in Swift Current. Expert guidance for energy workers, farmers, and small business owners.',
      keywords: ['Swift Current financial advisor', 'Swift Current financial planning', 'Southwest Saskatchewan wealth management', 'Swift Current investment']
    }
  }
];

// Export cities array with the name that matches the import in seo-routes
export const canadianCities = saskAlbertaCities;

// Helper function to get city by slug
export const getCityBySlug = (slug: string): CityData | undefined => {
  return saskAlbertaCities.find(city => city.slug === slug);
};

// Regional groups for Saskatchewan and Alberta
export const regionalGroups = [
  {
    name: 'Alberta',
    slug: 'alberta',
    description: 'Financial advisory services across Alberta, from Calgary to Medicine Hat.',
    cities: saskAlbertaCities.filter(city => city.provinceCode === 'AB').map(city => city.name)
  },
  {
    name: 'Saskatchewan',
    slug: 'saskatchewan',
    description: 'Expert financial planning throughout Saskatchewan, from Saskatoon to Swift Current.',
    cities: saskAlbertaCities.filter(city => city.provinceCode === 'SK').map(city => city.name)
  },
  {
    name: 'Prairie Provinces',
    slug: 'prairie',
    description: 'Comprehensive wealth management services across the Canadian Prairies.',
    cities: saskAlbertaCities.map(city => city.name)
  }
];